import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
    Phone, MessageSquare, Globe, Heart, Wind, Eye, 
    Sparkles, MapPin, Loader2, ExternalLink, AlertTriangle
} from 'lucide-react';

const ResourcesPage = () => {
    const { api } = useAuth();
    const [resources, setResources] = useState(null);
    const [localResources, setLocalResources] = useState(null);
    const [loadingLocal, setLoadingLocal] = useState(false);
    const [location, setLocation] = useState(null);

    useEffect(() => {
        fetchResources();
        // Track that this page was accessed (for child safety alerts)
        trackResourceAccess();
    }, []);

    const trackResourceAccess = async () => {
        try {
            await api.post('/resources/track-access');
        } catch (error) {
            // Silent fail - this is just for tracking
        }
    };

    const fetchResources = async () => {
        try {
            const response = await api.get('/resources/');
            setResources(response.data);
        } catch (error) {
            console.error('Failed to fetch resources:', error);
        }
    };

    const getLocalResources = async () => {
        if (!navigator.geolocation) {
            return;
        }

        setLoadingLocal(true);
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                setLocation({ latitude, longitude });
                
                try {
                    const response = await api.get(`/resources/local?latitude=${latitude}&longitude=${longitude}`);
                    setLocalResources(response.data);
                } catch (error) {
                    console.error('Failed to get local resources:', error);
                } finally {
                    setLoadingLocal(false);
                }
            },
            (error) => {
                console.error('Location error:', error);
                setLoadingLocal(false);
            }
        );
    };

    const exercises = resources?.self_help || [
        {
            name: "4-7-8 Breathing",
            type: "breathing",
            description: "Inhale for 4 seconds, hold for 7, exhale for 8. Repeat 4 times.",
            icon: Wind
        },
        {
            name: "5-4-3-2-1 Grounding",
            type: "grounding",
            description: "Name 5 things you see, 4 you hear, 3 you feel, 2 you smell, 1 you taste",
            icon: Eye
        },
        {
            name: "Body Scan",
            type: "relaxation",
            description: "Close your eyes and slowly focus attention from head to toe, releasing tension",
            icon: Sparkles
        }
    ];

    return (
        <DashboardLayout>
            <div data-testid="resources-page" className="max-w-4xl mx-auto space-y-8">
                <div className="text-center space-y-2">
                    <h1 className="font-heading text-3xl font-semibold text-foreground">
                        Support & Resources
                    </h1>
                    <p className="text-muted-foreground">
                        You're not alone. Help is available whenever you need it.
                    </p>
                </div>

                {/* Emergency Resources */}
                <Card className="border-destructive/30 bg-destructive/5">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2 text-destructive">
                            <AlertTriangle className="w-5 h-5" />
                            If You're in Crisis
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <a 
                                href="tel:988"
                                className="flex items-center gap-4 p-4 rounded-xl bg-background hover:bg-muted transition-colors"
                            >
                                <div className="w-12 h-12 bg-destructive/10 rounded-xl flex items-center justify-center">
                                    <Phone className="w-6 h-6 text-destructive" />
                                </div>
                                <div>
                                    <p className="font-semibold">National Suicide Prevention</p>
                                    <p className="text-2xl font-heading font-bold text-destructive">988</p>
                                    <p className="text-sm text-muted-foreground">Free, confidential, 24/7</p>
                                </div>
                            </a>
                            
                            <a 
                                href="sms:741741?body=HOME"
                                className="flex items-center gap-4 p-4 rounded-xl bg-background hover:bg-muted transition-colors"
                            >
                                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                                    <MessageSquare className="w-6 h-6 text-primary" />
                                </div>
                                <div>
                                    <p className="font-semibold">Crisis Text Line</p>
                                    <p className="text-lg font-medium">Text HOME to 741741</p>
                                    <p className="text-sm text-muted-foreground">24/7 crisis support via text</p>
                                </div>
                            </a>
                        </div>
                        
                        <a 
                            href="https://www.iasp.info/resources/Crisis_Centres/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                            <Globe className="w-4 h-4" />
                            Find international crisis centers
                            <ExternalLink className="w-3 h-3" />
                        </a>
                    </CardContent>
                </Card>

                {/* Quick Exercises */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <Heart className="w-5 h-5 text-chart-5" />
                            Quick Calming Exercises
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {exercises.map((exercise, index) => (
                                <motion.div
                                    key={exercise.name}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        {exercise.icon ? (
                                            <exercise.icon className="w-5 h-5 text-primary" />
                                        ) : (
                                            <Wind className="w-5 h-5 text-primary" />
                                        )}
                                        <h3 className="font-medium">{exercise.name}</h3>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {exercise.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* General Resources */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl">
                            Mental Health Resources
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {resources?.general?.map((resource, index) => (
                            <div 
                                key={index}
                                className="flex items-start gap-4 p-4 rounded-xl bg-muted/30"
                            >
                                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                    {resource.phone ? <Phone className="w-5 h-5 text-primary" /> : <Globe className="w-5 h-5 text-primary" />}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-medium">{resource.name}</h3>
                                    {resource.phone && (
                                        <a href={`tel:${resource.phone}`} className="text-primary font-medium">
                                            {resource.phone}
                                        </a>
                                    )}
                                    {resource.url && (
                                        <a 
                                            href={resource.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary flex items-center gap-1"
                                        >
                                            Visit website <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {resource.description}
                                    </p>
                                </div>
                            </div>
                        )) || (
                            <>
                                <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30">
                                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                        <Phone className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium">SAMHSA National Helpline</h3>
                                        <a href="tel:1-800-662-4357" className="text-primary font-medium">
                                            1-800-662-4357
                                        </a>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Free, confidential treatment referrals and information
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30">
                                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                                        <Phone className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium">NAMI Helpline</h3>
                                        <a href="tel:1-800-950-6264" className="text-primary font-medium">
                                            1-800-950-6264
                                        </a>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            National Alliance on Mental Illness support line
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Local Resources */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <MapPin className="w-5 h-5" />
                            Find Local Support
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!localResources ? (
                            <div className="text-center py-6">
                                <p className="text-muted-foreground mb-4">
                                    Get AI-suggested mental health resources near you
                                </p>
                                <Button
                                    onClick={getLocalResources}
                                    disabled={loadingLocal}
                                    className="rounded-full"
                                    data-testid="get-local-resources"
                                >
                                    {loadingLocal ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Finding resources...
                                        </>
                                    ) : (
                                        <>
                                            <MapPin className="w-4 h-4 mr-2" />
                                            Use My Location
                                        </>
                                    )}
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm text-muted-foreground">
                                    Resources near {localResources.location}
                                </p>
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                    <div className="whitespace-pre-wrap text-foreground">
                                        {localResources.suggestions}
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {localResources.note}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Disclaimer */}
                <p className="text-center text-sm text-muted-foreground">
                    MoodTrack is not a substitute for professional mental health care. 
                    If you're experiencing a mental health emergency, please contact emergency services or a crisis hotline.
                </p>
            </div>
        </DashboardLayout>
    );
};

export default ResourcesPage;
